import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Row, Col, Modal, Tab, Nav } from "react-bootstrap";
import { useRef } from "react";
import { FileEarmarkArrowUp, FileEarmarkText } from "react-bootstrap-icons";
import {
  platformList as PLATFORM_OPTIONS_LIST,
  financingList as FINANCING_OPTIONS_LIST,
} from "@/utils/constant";
import DocumentUploadForm from '../components/document_upload_form';
import { uploadEachFile, describeUploadFailures } from '../components/upload_utils';
import {
  uploadDocumentRequest,
  updateLoanRequest,
} from "@/services";
import { formatApiErrorMessage } from "@/services/_apiConfig";
import "./missing_docs_modal.css";

const VAT_NUMBER_LENGTH = 15;
const LOAN_AMOUNT_MIN = 50000;
const LOAN_AMOUNT_MAX = 4000000;

const GENERAL_TAB = "general";
const ADDITIONAL_TAB = "additional";
const ADDITIONAL_DOCUMENT_KEYS = [
  "mol_wps",
  "facility_letters",
  "receivables_ageing",
  "payables_ageing",
  "top_customers_suppliers",
  "business_contracts",
  "buyer_invoices",
  "ytd_financials",
];
const GENERAL_FIELD_KEYS = [
  "type_of_financing",
  "platform",
  "platform_name",
  "loan_amount_requested",
  "files_vat",
  "vat_number",
];

const formatYearLabel = (year) => (/^FY\d{4}$/.test(year) ? `FY ${year.slice(2)}` : year);

const formatAed = (value) => Number(value).toLocaleString("en-US");

// Loan amount is kept as a plain digit string in state and shown with separators.
const toAmountDigits = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.round(n)) : String(value).replace(/\D/g, "");
};

const DocumentCollectionForm = ({ loanDetails, reloadLoanDetails, documentTypesList, documentsList, reloadDocumentsList, loading, afterSubmit }) => {
  const [formData, setFormData] = useState({
    platform: loanDetails?.platform || '',
    platform_name: loanDetails?.platform_name || '',
    type_of_financing: loanDetails?.type_of_financing || '',
    loan_amount_requested: toAmountDigits(loanDetails?.loan_amount_requested),
    files_vat: loanDetails?.files_vat === true ? 'true' : loanDetails?.files_vat === false ? 'false' : '',
    vat_number: loanDetails?.vat_number || '',
    status: 'completed'
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMissingDocsModal, setShowMissingDocsModal] = useState(false);
  const [missingDocTitles, setMissingDocTitles] = useState([]);
  const [activeTab, setActiveTab] = useState(GENERAL_TAB);
  const isLoanVerified = loanDetails.status === 'verified';

  const generalDocTypes = (documentTypesList || []).filter(
    (doc) => !ADDITIONAL_DOCUMENT_KEYS.includes(doc.document_type_key)
  );
  const additionalDocTypes = (documentTypesList || []).filter(
    (doc) => ADDITIONAL_DOCUMENT_KEYS.includes(doc.document_type_key)
  );

  const loanAmountRef = useRef(null);
  const financingRef = useRef(null);
  const platformRef = useRef(null);
  const filesVatRef = useRef(null);
  const vatNumberRef = useRef(null);
  const platformNameRef = useRef(null);

  const onFileUpload = async (docType, event, afterEventCallback, metadata = null) => {
    const selectedFiles = Array.from(event.target.files);
    await uploadFile(docType, selectedFiles, metadata);
    if (afterEventCallback) afterEventCallback(docType);
  };

  const uploadFile = async (docType, selectedFiles, metadata) => {
    setUploading(true);
    setErrors({});

    try {
      const doc = documentTypesList.find(doc => doc.document_type === docType);
      const failures = await uploadEachFile(selectedFiles, (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append("document_type_id", doc.document_type);
        formDataUpload.append("file", file);
        formDataUpload.append('loan_id', loanDetails?.uuid);

        if (metadata) {
          formDataUpload.append("metadata", JSON.stringify(metadata));
        }
        return uploadDocumentRequest(formDataUpload);
      });
      if (failures.length) {
        setErrors({ general: describeUploadFailures(failures, selectedFiles.length) });
      }
      await reloadDocumentsList();
    } catch (err) {
      setErrors({
        general: err?.message || "Failed to Upload Document. Try again.",
      });
    } finally {
      setUploading(false);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "loan_amount_requested") {
      setFormData((prev) => ({
        ...prev,
        loan_amount_requested: value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 10),
      }));
      return;
    }
    if (name === "vat_number") {
      setFormData((prev) => ({
        ...prev,
        vat_number: value.replace(/\D/g, "").slice(0, VAT_NUMBER_LENGTH),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "files_vat" && value !== "true" ? { vat_number: "" } : {}),
    }));
  };

  const hasUploadedFiles = (documentTypeId) => {
    return (documentsList || [])
      .filter((document) => document.document_type === documentTypeId)
      .flatMap((d) => d.document_files || [])
      .length > 0;
  };

  // Mirrors the backend rules so the asterisks follow the amount and product
  // currently entered, not the values saved on the loan.
  const isDocRequired = (doc) => {
    if (doc.document_type_key === "vat_returns") return formData.files_vat === "true";
    if (doc.mandatory_above_loan_amount != null) {
      return Number(formData.loan_amount_requested || 0) > doc.mandatory_above_loan_amount;
    }
    if (doc.mandatory_when_financing) {
      return formData.type_of_financing === doc.mandatory_when_financing;
    }
    return !!doc.is_mandatory;
  };

  const orderedAdditionalDocTypes = [...additionalDocTypes].sort(
    (a, b) => Number(isDocRequired(b)) - Number(isDocRequired(a))
  );

  const missingRequiredDocMessage = (doc) => {
    const docs = (documentsList || []).filter((d) => d.document_type === doc.document_type);
    switch (doc.document_type_key) {
      case "audited_financials": {
        const missingYears = (doc.required_financial_years || []).filter(
          (year) => !docs.some((d) => d.metadata?.financial_year === year && (d.document_files || []).length)
        );
        return missingYears.length
          ? `Upload the financial statements for ${missingYears.map(formatYearLabel).join(", ")}`
          : null;
      }
      case "buyer_invoices":
        return docs.some((d) => d.metadata?.buyer_name?.trim() && (d.document_files || []).length)
          ? null
          : "Please add a buyer and upload sample invoice copies";
      case "bank_statements":
        return hasUploadedFiles(doc.document_type) ? null : "Bank Statements are required";
      case "vat_returns":
        return hasUploadedFiles(doc.document_type) ? null : "VAT Returns are required when you file VAT";
      default:
        return hasUploadedFiles(doc.document_type) ? null : `${doc.title} is required`;
    }
  };

  const collectDocErrors = (docTypes) => {
    const docErrors = {};
    docTypes.filter(isDocRequired).forEach((doc) => {
      const message = missingRequiredDocMessage(doc);
      if (message) docErrors[doc.document_type_key] = message;
    });
    return docErrors;
  };

  const getMissingOptionalDocs = () => {
    return (documentTypesList || [])
      .filter(doc => !isDocRequired(doc))
      .filter(doc => !hasUploadedFiles(doc.document_type))
      .map(doc => doc.title);
  };

  const tabForErrorKey = (key) =>
    ADDITIONAL_DOCUMENT_KEYS.includes(key) ? ADDITIONAL_TAB : GENERAL_TAB;

  const focusFirstError = (newErrors) => {
    const fieldRefs = {
      type_of_financing: financingRef,
      platform: platformRef,
      platform_name: platformNameRef,
      loan_amount_requested: loanAmountRef,
      files_vat: filesVatRef,
      vat_number: vatNumberRef,
    };
    const orderedKeys = [
      ...GENERAL_FIELD_KEYS,
      ...generalDocTypes.map((doc) => doc.document_type_key),
      ...orderedAdditionalDocTypes.map((doc) => doc.document_type_key),
    ];
    const firstKey = orderedKeys.find((key) => newErrors[key]);
    if (!firstKey) return;

    setActiveTab(tabForErrorKey(firstKey));
    // Wait for the tab pane to become visible before scrolling into it.
    setTimeout(() => {
      const target = fieldRefs[firstKey]?.current || document.getElementById(`doc-section-${firstKey}`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus?.();
    }, 0);
  };

  const submitLoan = async () => {
    setSubmitting(true);
    setErrors({});
    try {
      const res = await updateLoanRequest({
        ...formData,
        loan_amount_requested: Number(formData.loan_amount_requested),
        files_vat: formData.files_vat === "true" ? true : formData.files_vat === "false" ? false : null,
        vat_number: formData.files_vat === "true" ? formData.vat_number.trim() : null,
      }, loanDetails?.id);

      if (res?.success === false) {
        const fieldErrors = {};
        Object.entries(res.error || {}).forEach(([key, value]) => {
          if (key === "status" || key === "message") return;
          fieldErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrors({
          ...fieldErrors,
          general: formatApiErrorMessage(res.error, res.message || "Something went wrong"),
        });
        focusFirstError(fieldErrors);
        return;
      }

      await reloadLoanDetails();
      if (afterSubmit) afterSubmit();
    } catch (err) {
      const fieldError = err?.vat_returns || err?.files_vat || err?.vat_number;
      const firstFieldError = Array.isArray(fieldError) ? fieldError[0] : fieldError;
      setErrors({
        general: firstFieldError || err?.message || "Failed to submit KYC. Try again.",
        ...(err?.vat_returns ? { vat_returns: firstFieldError } : {}),
        ...(err?.files_vat ? { files_vat: Array.isArray(err.files_vat) ? err.files_vat[0] : err.files_vat } : {}),
        ...(err?.vat_number ? { vat_number: Array.isArray(err.vat_number) ? err.vat_number[0] : err.vat_number } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const validateGeneralTab = () => {
    const newErrors = {};
    if (!formData.type_of_financing) newErrors.type_of_financing = "Financing Type is required";
    const isSID = formData.type_of_financing === 'invoice';
    if (isSID && !formData.platform) newErrors.platform = "Platform is required";
    if (isSID && formData?.platform === 'other' && !formData.platform_name) newErrors.platform_name = "Platform Name is required";
    const loanAmount = Number(formData.loan_amount_requested);
    if (!formData.loan_amount_requested) {
      newErrors.loan_amount_requested = "Requested loan amount is required";
    } else if (loanAmount < LOAN_AMOUNT_MIN || loanAmount > LOAN_AMOUNT_MAX) {
      newErrors.loan_amount_requested = `Enter an amount between AED ${formatAed(LOAN_AMOUNT_MIN)} and AED ${formatAed(LOAN_AMOUNT_MAX)}`;
    }
    if (formData.files_vat !== "true" && formData.files_vat !== "false") {
      newErrors.files_vat = "Select whether you file VAT";
    } else if (formData.files_vat === "true") {
      const vatNumber = formData.vat_number.trim();
      if (!vatNumber) {
        newErrors.vat_number = "VAT number is required when you file VAT";
      } else if (!/^\d{15}$/.test(vatNumber)) {
        newErrors.vat_number = `Enter a valid ${VAT_NUMBER_LENGTH}-digit VAT TRN`;
      }
    }

    return { ...newErrors, ...collectDocErrors(generalDocTypes) };
  };

  const handleContinueToAdditional = () => {
    if (!isLoanVerified) {
      const newErrors = validateGeneralTab();
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) {
        focusFirstError(newErrors);
        return;
      }
    }
    setActiveTab(ADDITIONAL_TAB);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoanVerified) {
      // handle close
      if (afterSubmit) afterSubmit();
      return;
    }

    const newErrors = { ...validateGeneralTab(), ...collectDocErrors(additionalDocTypes) };
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      return;
    }

    // Mandatory checks passed. Warn if optional documents are still missing.
    const missing = getMissingOptionalDocs();
    if (missing.length > 0) {
      setMissingDocTitles(missing);
      setSubmitting(false);
      setShowMissingDocsModal(true);
      return;
    }

    await submitLoan();
  };

  const handleContinueSaving = async () => {
    setShowMissingDocsModal(false);
    await submitLoan();
  };

  return (
    <Form
      onSubmit={handleSubmit}
      className="w-full"
      style={{ width: '100%' }}
    >
      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey={GENERAL_TAB} data-testid="fincobox-upload-tab-general">
              Application Details
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={ADDITIONAL_TAB} data-testid="fincobox-upload-tab-additional">
              Additional Documents
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey={GENERAL_TAB}>
            <fieldset disabled={isLoanVerified}>
              <Row>
                <Col span={6}>
                  <Form.Group className="mb-3" controlId="doc-collection-type-of-financing">
                    <Form.Label>
                      Type of Financing <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      ref={financingRef}
                      name="type_of_financing"
                      value={formData.type_of_financing}
                      onChange={handleChange}
                      isInvalid={!!errors.type_of_financing}
                      data-testid="fincobox-upload-financing-type"
                    >
                      <option value="">-- Select Financing Type --</option>
                      {FINANCING_OPTIONS_LIST.map(financeOption => {
                        return (
                          <option key={financeOption.value} value={financeOption.value} data-option-value={financeOption.value}>
                            {financeOption.label}
                          </option>
                        );
                      })}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.type_of_financing}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col span={6}>
                  <Form.Group className="mb-3" controlId="doc-collection-platform">
                    <Form.Label>
                      Sales Platform {formData.type_of_financing === 'invoice' && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Select
                      ref={platformRef}
                      name="platform"
                      value={formData.platform}
                      onChange={handleChange}
                      isInvalid={!!errors.platform}
                      data-testid="fincobox-upload-sales-platform"
                    >
                      <option value="">-- Select Platform --</option>
                      {
                        PLATFORM_OPTIONS_LIST.map(platformOption => {
                          return (
                            <option key={platformOption.value} value={platformOption.value} data-option-value={platformOption.value}>
                              {platformOption.label}
                            </option>
                          );
                        })
                      }
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.platform}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {
                  formData.platform === 'other' && (
                    <Col span={6}>
                      <Form.Group className="mb-3" controlId="doc-collection-platform-name">
                        <Form.Label>
                          Platform Name {formData.type_of_financing === 'invoice' && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                          ref={platformNameRef}
                          name="platform_name"
                          value={formData.platform_name}
                          onChange={handleChange}
                          isInvalid={!!errors.platform_name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.platform_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  )
                }
              </Row>

              <Row>
                <Col span={6}>
                  <Form.Group className="mb-3" controlId="doc-collection-loan-amount">
                    <Form.Label>
                      Requested Loan Amount (in AED) <span className="text-danger">*</span>
                    </Form.Label>

                    <Form.Control
                      ref={loanAmountRef}
                      type="text"
                      name="loan_amount_requested"
                      inputMode="numeric"
                      autoComplete="off"
                      value={formData.loan_amount_requested ? formatAed(formData.loan_amount_requested) : ""}
                      onChange={handleChange}
                      placeholder="e.g. 250,000"
                      isInvalid={!!errors.loan_amount_requested}
                      aria-describedby="doc-collection-loan-amount-help"
                      data-testid="fincobox-upload-loan-amount"
                    />
                    <Form.Text id="doc-collection-loan-amount-help" muted>
                      Between AED {formatAed(LOAN_AMOUNT_MIN)} and AED {formatAed(LOAN_AMOUNT_MAX)}
                    </Form.Text>

                    <Form.Control.Feedback type="invalid">
                      {errors.loan_amount_requested}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col span={6}>
                  <fieldset className="mb-3" data-testid="fincobox-upload-files-vat">
                    <legend className="form-label fs-6 float-none mb-2">
                      Do you file VAT? <span className="text-danger">*</span>
                    </legend>
                    <Form.Check
                      ref={filesVatRef}
                      inline
                      type="radio"
                      id="doc-collection-files-vat-yes"
                      name="files_vat"
                      value="true"
                      label="Yes"
                      checked={formData.files_vat === "true"}
                      onChange={handleChange}
                      isInvalid={!!errors.files_vat}
                      data-testid="fincobox-upload-files-vat-yes"
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="doc-collection-files-vat-no"
                      name="files_vat"
                      value="false"
                      label="No"
                      checked={formData.files_vat === "false"}
                      onChange={handleChange}
                      isInvalid={!!errors.files_vat}
                      data-testid="fincobox-upload-files-vat-no"
                    />
                    {errors.files_vat && (
                      <div className="invalid-feedback d-block">{errors.files_vat}</div>
                    )}
                  </fieldset>
                </Col>
                {formData.files_vat === "true" && (
                  <Col span={6}>
                    <Form.Group className="mb-3" controlId="doc-collection-vat-number">
                      <Form.Label>
                        VAT Number <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        ref={vatNumberRef}
                        type="text"
                        inputMode="numeric"
                        name="vat_number"
                        maxLength={VAT_NUMBER_LENGTH}
                        value={formData.vat_number}
                        onChange={handleChange}
                        placeholder="15-digit TRN"
                        isInvalid={!!errors.vat_number}
                        data-testid="fincobox-upload-vat-number"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.vat_number}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}
              </Row>


              {/* Documents */}
              <DocumentUploadForm
                documentTypes={generalDocTypes}
                documentsList={documentsList}
                reloadDocumentsList={reloadDocumentsList}
                showLoader={loading}
                onChange={onFileUpload}
                errors={errors}
                isRequired={isDocRequired}
                uploading={uploading}
                onUploadingChange={setUploading}
              />
            </fieldset>
          </Tab.Pane>
          <Tab.Pane eventKey={ADDITIONAL_TAB}>
            <fieldset disabled={isLoanVerified}>
              <DocumentUploadForm
                documentTypes={orderedAdditionalDocTypes}
                documentsList={documentsList}
                reloadDocumentsList={reloadDocumentsList}
                showLoader={loading}
                onChange={onFileUpload}
                errors={errors}
                isRequired={isDocRequired}
                uploading={uploading}
                onUploadingChange={setUploading}
              />
            </fieldset>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {errors.general && <Alert variant="danger">{errors.general}</Alert>}
      {errors.vat_returns && <Alert variant="danger">{errors.vat_returns}</Alert>}
      {/* ---- Actions ---- */}
      <div className="d-flex justify-content-end align-items-center gap-2">
        {activeTab === GENERAL_TAB ? (
          <Button
            type="button"
            variant="primary"
            onClick={handleContinueToAdditional}
            disabled={uploading}
            data-testid="fincobox-upload-continue"
          >
            {uploading ? "Uploading..." : "Continue"}
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setActiveTab(GENERAL_TAB)}
              disabled={uploading}
              data-testid="fincobox-upload-back"
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || uploading}
              data-testid="fincobox-upload-save-continue"
            >
              {submitting ? "Saving..." : uploading ? "Uploading..." : isLoanVerified ? "Close" : "Save & Continue"}
            </Button>
          </>
        )}
      </div>

      {/* Reminder popup when optional documents are missing */}
      <Modal
        className="missingDocs_modal"
        backdropClassName="missingDocs_backdrop"
        show={showMissingDocsModal}
        onHide={() => setShowMissingDocsModal(false)}
        backdrop="static"
        keyboard={false}
        centered
        aria-labelledby="missing-docs-modal-title"
        data-testid="fincobox-missing-docs-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="missing-docs-modal-title">
            Upload remaining documents?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="missingDocs_lead">
            Uploading the remaining documents now will help us verify and
            process your application faster.
          </p>
          {missingDocTitles.length > 0 && (
            <div className="missingDocs_panel">
              <p className="missingDocs_panelTitle">Documents still pending</p>
              <ul className="missingDocs_list">
                {missingDocTitles.map((title) => (
                  <li key={title}>
                    <FileEarmarkText />
                    <span>{title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            className="missingDocs_secondaryBtn"
            onClick={handleContinueSaving}
            disabled={submitting || uploading}
            data-testid="fincobox-missing-docs-continue"
          >
            {submitting ? "Saving..." : "Save anyway"}
          </Button>
          <Button
            variant="primary"
            className="missingDocs_primaryBtn"
            onClick={() => setShowMissingDocsModal(false)}
            data-testid="fincobox-missing-docs-go-back"
          >
            Go back & upload
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export default DocumentCollectionForm;
